import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { registerClass, currySC, IFNTheme, cns } from '@food/css-manager';
import { DefaultLayout } from '../utils/layout';

interface IEditorialSectionOrCollectionProps {
    layout: DefaultLayout | string | null | undefined;
    media?: string | null | undefined,
    color?: string | null | undefined,
    title?: string | null | undefined,
    links?: { name: string, target?: string, url: string, iframe?: boolean | undefined }[];
    logoLink?: string;
}

const styleClass = registerClass(
    (t: IFNTheme) => `
        display: flex;
        align-items: stretch;
        justify-content: flex-start;
        
        &.${sc('layout', DefaultLayout.TABLET)}, &.${sc('layout', DefaultLayout.MOBILE)} {
            flex-wrap: wrap;
        }

        .${sc('contents')} {
            padding: 1.5em;
            background-color: ${t.colors.primary.plain};
            background-image: linear-gradient(135deg, rgba(0, 0, 0, 0.15) 10%, transparent 10%, transparent 50%, rgba(0, 0, 0, 0.15) 50%, rgba(0, 0, 0, 0.15) 60%, transparent 60%, transparent 100%);
            background-size: 13px 13px;
            flex-grow: 1;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;

            .${sc('back')} {
                font-size: 1.2em;
                
                > * {
                    cursor: pointer;

                    &::before {
                        font-family: Material Icons;
                        content: 'arrow_back_ios';
                        width: 1em;
                        overflow: hidden;
                        display: inline-block;
                        vertical-align: middle;
                        margin-right: ${(t.ratios.xs / 2) * t.font.baseSize}px;
                    }

                    color: ${t.colors.white};
                    font-weight: 700;
                }
            }

            .${sc('links')} {
                display: flex;
                flex-direction: column;
                position: absolute;
                right: 1em;
                bottom: 1em;

                color: ${t.colors.white};
                font-weight: 600;
                text-align: right;
                text-decoration: underline;
                font-size: 90%;

                > * {
                    cursor: pointer;
                    margin-top: 0.5em;
                }
            }
        }

        &.${sc('text')} {

            .${sc('contents')} {
                .${sc('back')} {
                    position: absolute;
                    left: 0.5em;
                    top: 50%;
                    transform: translateY(-50%);
                }

                .${sc('heading')} {
                    .${sc('textWrapper')} {
                        font-weight: 700;
                        font-size: 280%;
                        line-height: 1.3;
                        font-family: ${t.font.family};
                        color: ${t.colors.white};
                    }
                }
            }
        }

        &.${sc('image')} {
            .${sc('contents')} {
                .${sc('back')} {
                    position: absolute;
                    right: 0.5em;
                    top: 0.5em;
                }
            }
            .${sc('heading')} {
                padding-right: 1em;
                img {
                    height: 7em;
                    display: block;
                }
            }
        }

        &.${sc('layout-mobile')} {
            flex-direction: column;
            align-items: center;

            &.${sc('image')} {
                .${sc('contents')} {
                    background: none!important;
                    position: inherit;
                    color: ${t.colors.ink};
                    padding: 0.5em;

                    .${sc('back')} {
                        position: absolute;
                        left: 1em;
                        top: 1em;
                        right: auto;
                        font-size: 80%;

                        * {
                            color: ${t.colors.ink};
                        }
                    }

                    .${sc('links')} {
                        position: relative;
                        right: auto;
                        bottom: auto;
                        color: ${t.colors.ink};
                        text-align: center;
                        font-size: 80%;
                    }
                }
                .${sc('heading')} {
                    padding: 2em 0 0 0;
                    img {
                        height: 6em;
                        display: block;
                    }
                }
            }

            &.${sc('text')} {

                .${sc('contents')} {
                    width: 100%;
                    background: none!important;
                    flex-direction: column;
                    padding: 2em 0 0;

                    .${sc('back')} {
                        left: 1em;
                        top: 1em;
                        transform: none;
                        font-size: 80%;

                        * {
                            color: ${t.colors.ink};
                        }
                    }
    
                    .${sc('heading')} {
                        .${sc('textWrapper')} {
                            font-size: 250%;
                            color: ${t.colors.ink};
                        }
                    }

                    .${sc('links')} {
                        position: relative;
                        right: auto;
                        bottom: auto;
                        color: ${t.colors.ink};
                        text-align: center;
                        padding: 1em;
                        font-size: 80%;
                    }
                }
            }
        }

        &.${sc('layout-tablet')} {
            &.${sc('image')},
            &.${sc('text')} {
                .${sc('heading')} {
                    .${sc('textWrapper')} {
                        font-size: 160%;
                    }
                }
            }
        }

        .${sc('iframeOverlay')} {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.5);
            z-index: 100;
            display: none;

            &.open {
                display: block;
            }
    
            .${sc('box')} {
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%,-50%);
                padding: 40px 40px 0px;
                background: ${t.colors.white};
                border-radius: 20px;
                width: 700px;
                max-width: calc(100vw - 2em);
                max-height: calc(100vh - 5em);
                overflow: auto;
                -webkit-box-shadow: 0px 0px 15px 1px rgba(0,0,0,0.73); 
                box-shadow: 0px 0px 15px 1px rgba(0,0,0,0.73);

                .${sc('close_x')} {
                    position: absolute;
                    right: 10px;
                    top: 10px;
                    border-radius: 5px 5px 5px 5px;
                    box-shadow: 0 -3px rgb(0 0 0 / 10%) inset;
                    color: #FFF;
                    font-family: proxima-nova, sans-serif;
                    font-weight: 700;
                    font-size: 12px;
                    opacity: 1;
                    line-height: 1;
                    display: inline-block;
                    border-width: 0px !important;
                    background: linear-gradient(125deg,#E20613,#E20613);
                    width: 38px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
    
                    &:hover {
                        background: linear-gradient(125deg,#FF353A,#FF353A);
                        box-shadow: 0 20px 38px rgb(0 0 0 / 16%);
                        transform: translateY(-3px);
                        transition-duration: .2s;
                        transition-timing-function: ease-in-out;
                    }
                }
    
                h1 {
                    text-align: center;
                    font-weight: ${t.font.weights.semibold};
                    font-size: ${t.font.baseSize * t.ratios.xl}px;
                    margin-bottom: 15px;
                }

                iframe {
                    height: 350px;
                    width: 100%;
                }
    
            }
        }
  `,
);

const sc = currySC(styleClass);

const EditorialSectionOrCollectionHeader: React.FC<IEditorialSectionOrCollectionProps> = ({
    media,
    color,
    title,
    links,
    layout,
    logoLink
}) => {

    let preloadedLinkIframe = '';
    const linksIframe = links?.filter((v) => v.iframe);
    if (linksIframe?.length) {
        preloadedLinkIframe = linksIframe[0].url;
    }
    const [iframeOverlay, setiframeOverlay] = useState({
        open: false,
        url: preloadedLinkIframe
    });

    const history = useHistory();

    const goBack = () => {
        if ((window as any).historyLocations.length) {
            const lastPage = (window as any).historyLocations[(window as any).historyLocations.length - 1];
            if (lastPage.pathname.indexOf('/category/') > -1 && (window as any).location.href.indexOf('/category/') > -1) {
                history.push("/");
            } else {
                history.goBack();
            }
        } else {
            history.push("/");
        }
    }

    return (
        <>

            < div className={cns(styleClass, media ? sc('image') : sc('text'), sc('layout-' + layout))
            }>
                <div className={cns(sc('iframeOverlay'), iframeOverlay.open ? 'open' : null)}>
                    <div className={sc('box')}>
                        <div className={sc('close_x')} onClick={() => setiframeOverlay({ open: false, url: preloadedLinkIframe })} style={color ? { background: color } : {}}>X</div>
                        <h1>Iscriviti alla newsletter</h1>
                        <iframe src={iframeOverlay.url}></iframe>
                    </div>
                </div>

                {media && (
                    <Link to={logoLink ? logoLink : '/'} className={sc('heading')}>
                        <img src={media} />
                    </Link>
                )}
                <div className={sc('contents')} style={color ? { backgroundColor: color } : {}}>
                    <div className={sc('back')}>
                        <span onClick={goBack}>
                            Indietro
                        </span>
                    </div>

                    {
                        !media && (
                            <Link to={logoLink ? logoLink : '/'} className={sc('heading')}>
                                <div className={sc('textWrapper')}>{title}</div>
                            </Link>
                        )
                    }

                    {links && (
                        <div className={sc('links')}>
                            {links.map((v) => {
                                if (v.iframe) {
                                    return (
                                        <span onClick={() => setiframeOverlay({ open: true, url: v.url })} key={v.name}>{v.name}</span>
                                    );
                                }
                                return (
                                    <a href={v.url} target={v.target || '_self'} key={v.name}>{v.name}</a>
                                );
                            })}
                        </div>
                    )}

                </div>
            </div >
        </>
    );
};
export { EditorialSectionOrCollectionHeader };
